// WorkflowStepper.jsx — v6.45.1 Fool-proof step-by-step guide
// =============================================================
// Lives at the top of OrderDrawer. Shows an 8-pill horizontal
// stepper that lights up as the order progresses, plus a focused
// "action card" below for the CURRENT step. Future pills are
// locked (greyed, non-clickable) so ops cannot skip ahead.
// Completed pills are tappable to review what was done.
//
// The 8 macro steps:
//   1. 📋 Review       — verify buyer + PO + ship-to before authorize
//   2. 🔐 Authorize    — allocate serial, set shared password, launch build
//   3. ⚙️ Build         — wait for 36 verifiers + 53 pytests (~2 min)
//   4. ⬇️ Download      — pull the .tar.gz + SHA sidecar to the ops Mac
//   5. 🧪 Physical QC   — flash a stick, run the 10-item hardware checklist
//   6. 📦 Pack          — assemble the retail box (4 inserts + stickers)
//   7. 🚚 Ship          — record carrier + tracking number
//   8. ✅ Close         — buyer confirmed receipt, order terminates
//
// Design intent:
//   * One obvious action per step. "What am I waiting for?" never
//     requires asking a teammate.
//   * The two intermediate ops confirmations (Downloaded, Packed) are
//     persisted in localStorage keyed by order_id so a browser refresh
//     doesn't lose the operator's place.
import React, { useMemo } from "react";

const LS_KEY = (orderId) => `mosaic_usb_wf_marks_${orderId}`;

export const readMarks = (orderId) => {
  try { return JSON.parse(localStorage.getItem(LS_KEY(orderId)) || "{}"); }
  catch { return {}; }
};
export const writeMarks = (orderId, marks) => {
  try { localStorage.setItem(LS_KEY(orderId), JSON.stringify(marks)); } catch {}
};

// Step metadata — kept outside the component so React doesn't re-create
// the array on every render.
const STEPS = [
  { id: "review",     n: 1, icon: "📋", label: "Review",     subtitle: "Verify buyer facts" },
  { id: "authorize",  n: 2, icon: "🔐", label: "Authorize",  subtitle: "Serial + password" },
  { id: "build",      n: 3, icon: "⚙️", label: "Build",      subtitle: "Verifiers + pytests" },
  { id: "download",   n: 4, icon: "⬇️", label: "Download",   subtitle: "Tarball + SHA" },
  { id: "qc",         n: 5, icon: "🧪", label: "Physical QC", subtitle: "10-tick checklist" },
  { id: "pack",       n: 6, icon: "📦", label: "Pack",       subtitle: "Retail box + inserts" },
  { id: "ship",       n: 7, icon: "🚚", label: "Ship",       subtitle: "Carrier + tracking" },
  { id: "close",      n: 8, icon: "✅", label: "Close",      subtitle: "Buyer confirmed" },
];

// Palette per state. Outlined pills, not neon, matches the retail box.
const C = {
  done:     { bg: "#DCFCE7", fg: "#166534", border: "#16A34A", dot: "#16A34A" },
  current:  { bg: "#DBEAFE", fg: "#1E40AF", border: "#2563EB", dot: "#2563EB" },
  locked:   { bg: "#F1F5F9", fg: "#94A3B8", border: "#CBD5E1", dot: "#CBD5E1" },
  failed:   { bg: "#FEE2E2", fg: "#991B1B", border: "#DC2626", dot: "#DC2626" },
};

/**
 * Derive which macro step is currently active based on the order
 * document + ops localStorage marks. Returns:
 *   { current: step_id, states: { step_id: "done" | "current" | "locked" | "failed" } }
 */
export const deriveWorkflowState = (order, marks) => {
  if (!order) return { current: "review", states: {} };
  const s = {};
  // Start with everything locked
  STEPS.forEach((st) => { s[st.id] = "locked"; });

  const status = order.status;
  const drives = order.drives || [];
  const anyDriveBuilt     = drives.length > 0 && drives.every((d) => !!d.built_at);
  const anyDriveFailed    = drives.some((d) => d.status === "build_failed");
  const allDrivesQcPassed = drives.length > 0 && drives.every((d) => d.status === "qc_passed");

  // 1. Review is "done" the instant we're past "new" OR the operator
  //    has at least seen the drawer (we flag it done when buyer_frozen
  //    is true, since that only flips at Authorize-time).
  const reviewDone = ["authorized","building","build_failed","qc_pending",
                      "qc_passed","qc_failed","shipped","closed"].includes(status)
                     || order.buyer_frozen;
  s.review = reviewDone ? "done" :
             ["new","incomplete","on_hold"].includes(status) ? "current" : "locked";

  // 2. Authorize done once drives are allocated
  if (drives.length > 0) s.authorize = "done";
  else if (reviewDone && ["new","build_failed","qc_failed"].includes(status))
    s.authorize = "current";

  // 3. Build done once every drive has built_at (success) or failed
  if (anyDriveBuilt && !anyDriveFailed) s.build = "done";
  else if (status === "building" || status === "authorized") s.build = "current";
  else if (anyDriveFailed || status === "build_failed") s.build = "failed";

  // 4. Download — operator-marked via localStorage
  const downloadDone = !!marks.downloaded_at;
  if (s.build === "done") {
    s.download = downloadDone ? "done" : "current";
  }

  // 5. Physical QC — unlocked once download complete, done once all
  //    drives are qc_passed
  if (downloadDone) {
    if (allDrivesQcPassed) s.qc = "done";
    else if (status === "qc_pending") s.qc = "current";
    else if (status === "qc_failed") s.qc = "failed";
    else s.qc = "current";
  }

  // 6. Pack — operator-marked via localStorage, unlocks after all QC passed
  const packDone = !!marks.packed_at;
  if (allDrivesQcPassed || status === "qc_passed") {
    s.pack = packDone ? "done" : "current";
  }

  // 7. Ship — unlocked once pack done
  if (packDone) {
    s.ship = status === "shipped" || status === "closed" ? "done" : "current";
  }

  // 8. Close — unlocked once shipped
  if (status === "shipped") s.close = "current";
  else if (status === "closed") s.close = "done";

  // Terminal states don't have a "current"
  if (status === "rejected") {
    STEPS.forEach((st) => { if (s[st.id] !== "done") s[st.id] = "locked"; });
  }

  const current = STEPS.find((st) => s[st.id] === "current")?.id || null;
  // v6.45.2 — If build failed, surface the Build step as the focused
  // action card (not "Workflow complete"). The operator needs an
  // obvious Retry affordance, not a dead-end message.
  const failedId = STEPS.find((st) => s[st.id] === "failed")?.id || null;
  return { current: current || failedId, states: s, failedStep: failedId };
};


/**
 * Workflow stepper + focused action card.
 *
 * Props:
 *   order          — the full order doc
 *   onAction       — callback({ actionId, extra? }) — the drawer wires
 *                    this up to its own authorize / ship / close handlers
 *   marks          — { downloaded_at, packed_at } from localStorage
 *   onMarksChange  — callback(marks) so the parent can refresh the view
 */
const WorkflowStepper = ({ order, onAction, marks, onMarksChange }) => {
  const { current, states, failedStep } = useMemo(
    () => deriveWorkflowState(order, marks),
    [order, marks],
  );

  if (!order) return null;

  const toggleMark = (key) => {
    const next = { ...marks };
    if (next[key]) delete next[key];
    else next[key] = new Date().toISOString();
    onMarksChange(next);
  };

  // When any step is in "failed" state, swap the Build action card
  // for a retry-focused variant that surfaces per-drive errors.
  const effectiveStep = failedStep || current;
  const effectiveMode = failedStep ? "failed" : "current";

  return (
    <div data-testid="workflow-stepper" style={{ marginBottom: 16 }}>
      {/* Pills row */}
      <div style={{
        display: "flex", alignItems: "stretch",
        gap: 4, overflowX: "auto", padding: "4px 0 10px",
      }}>
        {STEPS.map((st, i) => {
          const state = states[st.id] || "locked";
          const col = C[state];
          const isCurrent = state === "current";
          return (
            <React.Fragment key={st.id}>
              <div
                data-testid={`wf-pill-${st.id}`}
                data-state={state}
                style={{
                  flex: "0 0 auto",
                  minWidth: 92,
                  padding: "8px 10px",
                  background: col.bg,
                  border: `1.5px solid ${col.border}`,
                  borderRadius: 10,
                  textAlign: "center",
                  position: "relative",
                  boxShadow: isCurrent ? `0 0 0 3px ${col.border}22` : "none",
                  transition: "box-shadow .2s",
                }}
              >
                <div style={{
                  display: "flex", alignItems: "center",
                  justifyContent: "center", gap: 4,
                  fontSize: 14, marginBottom: 2,
                }}>
                  <span style={{
                    width: 18, height: 18, borderRadius: "50%",
                    background: col.dot, color: "#FFFFFF",
                    fontSize: 10, fontWeight: 800,
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                  }}>
                    {state === "done" ? "✓" :
                     state === "failed" ? "!" : st.n}
                  </span>
                  <span>{st.icon}</span>
                </div>
                <div style={{
                  fontSize: 10, fontWeight: 700, color: col.fg,
                  letterSpacing: ".02em",
                }}>
                  {st.label.toUpperCase()}
                </div>
                <div style={{ fontSize: 9, color: col.fg + "AA", marginTop: 1 }}>
                  {st.subtitle}
                </div>
              </div>
              {i < STEPS.length - 1 && (
                <div style={{
                  flex: "0 0 10px", alignSelf: "center",
                  borderTop: `2px dotted ${
                    states[STEPS[i + 1].id] === "locked" ? "#CBD5E1" : "#16A34A"
                  }`,
                  height: 2,
                }}/>
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Action card for the CURRENT (or FAILED) step */}
      {effectiveStep && (
        <ActionCard
          stepId={effectiveStep}
          mode={effectiveMode}
          order={order}
          marks={marks}
          onAction={onAction}
          onToggleMark={toggleMark}
        />
      )}

      {/* If no current step AND no failed step (terminal states) show summary */}
      {!effectiveStep && (
        <div
          data-testid="wf-complete"
          style={{
            padding: 14, background: order.status === "closed" ? "#DCFCE7" : "#F1F5F9",
            border: `1px solid ${order.status === "closed" ? "#16A34A" : "#CBD5E1"}`,
            borderRadius: 10, textAlign: "center",
            fontSize: 13, color: order.status === "closed" ? "#166534" : "#64748B",
          }}>
          {order.status === "closed" ? "✓ Order closed — buyer confirmed receipt." :
           order.status === "rejected" ? "✕ Order was rejected — see audit timeline for reason." :
           "Workflow complete."}
        </div>
      )}
    </div>
  );
};


// ─── The focused action card for the CURRENT step ──────────────────
const ActionCard = ({ stepId, mode = "current", order, marks, onAction, onToggleMark }) => {
  // v6.45.2 — Build-failed variant: surface the exact per-drive errors
  // and give ops a one-click Retry button that re-opens the Authorize
  // modal (which will re-allocate serials for only the failed slots
  // on the next Authorize round once build_failed → authorized is
  // allowed by the legal-transitions map).
  if (mode === "failed" && stepId === "build") {
    const failedDrives = (order.drives || []).filter(
      (d) => d.status === "build_failed",
    );
    return (
      <div
        data-testid="wf-action-card-build-failed"
        style={{
          padding: 16,
          background: "linear-gradient(180deg,#FEF2F2 0%,#FFFFFF 100%)",
          border: "1.5px solid #DC2626",
          borderRadius: 12,
          boxShadow: "0 2px 8px rgba(220,38,38,.08)",
        }}
      >
        <div style={{ display: "flex", gap: 12 }}>
          <div style={{
            fontSize: 28, lineHeight: 1,
            width: 44, height: 44, borderRadius: 10,
            background: "#FEE2E2", display: "flex",
            alignItems: "center", justifyContent: "center",
            flexShrink: 0,
          }}>⚠️</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#991B1B", marginBottom: 4 }}>
              Step 3 — Build Failed
            </div>
            <div style={{ fontSize: 12.5, color: "#334155", lineHeight: 1.55 }}>
              {failedDrives.length} drive{failedDrives.length === 1 ? "" : "s"}
              {" "}failed. Review the error{failedDrives.length === 1 ? "" : "s"} below,
              then click <em>Retry Build</em> — the pipeline will re-allocate a fresh
              serial for every failed drive. The buyer facts stay locked.
            </div>
          </div>
        </div>
        {failedDrives.map((d) => (
          <div
            key={d.serial}
            data-testid={`wf-failure-${d.serial}`}
            style={{
              marginTop: 10, padding: 10,
              background: "#FFFFFF", border: "1px solid #FCA5A5",
              borderRadius: 6, fontSize: 11,
            }}
          >
            <div style={{ fontFamily: "monospace", fontWeight: 700, color: "#991B1B", marginBottom: 4 }}>
              {d.serial}
            </div>
            <div style={{ color: "#64748B", wordBreak: "break-word" }}>
              {d.error || "No error detail captured"}
            </div>
            {d.log_tail && (
              <details
                data-testid={`wf-failure-log-${d.serial}`}
                style={{ marginTop: 8 }}
              >
                <summary style={{
                  cursor: "pointer",
                  fontWeight: 600,
                  color: "#991B1B",
                  fontSize: 11,
                }}>
                  Show build log (last {d.log_tail.split("\n").length} lines)
                </summary>
                <pre style={{
                  marginTop: 6,
                  padding: 8,
                  background: "#0F172A",
                  color: "#E2E8F0",
                  borderRadius: 4,
                  fontSize: 10,
                  lineHeight: 1.45,
                  maxHeight: 320,
                  overflow: "auto",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}>{d.log_tail}</pre>
              </details>
            )}
          </div>
        ))}
        <button
          data-testid="wf-cta-retry-build"
          onClick={() => onAction({ actionId: "authorize" })}
          style={{
            display: "block", width: "100%", marginTop: 12,
            padding: "11px 16px",
            background: "#DC2626", color: "#FFFFFF",
            border: "none",
            borderRadius: 8, fontSize: 13, fontWeight: 700,
            cursor: "pointer",
          }}
        >
          🔄 Retry Build — Re-allocate serials
        </button>
        <div style={{
          marginTop: 10, padding: "8px 10px",
          background: "#FEF9C3", borderRadius: 6,
          fontSize: 11, color: "#713F12",
          borderLeft: "3px solid #EAB308",
        }}>
          💡 Common causes: disk-full on the pod, stale serial folder, interrupted
          verifier run. Retry picks fresh serials automatically — no manual
          cleanup needed.
        </div>
      </div>
    );
  }

  const cards = {
    review: {
      icon: "📋",
      title: "Step 1 — Review order details",
      body: (
        <>
          Scan the <strong>Buyer</strong> card below. Confirm the PO number,
          the ship-to address, and the SKU + grades match the signed quote.
          If anything looks wrong, use <em>Hold</em> or <em>Reject</em> instead.
        </>
      ),
      cta: {
        label: "Looks good — Authorize now →",
        action: "authorize",
        tone: "blue",
      },
      hint: "Clicking Authorize freezes the buyer facts and allocates a drive serial atomically.",
    },
    authorize: {
      icon: "🔐",
      title: "Step 2 — Authorize & launch build",
      body: (
        <>
          Pick the grade band and the number of drives, then set <strong>one shared
          password</strong> that will be printed on the retail scratch-off label.
          Click <em>Generate</em> for a policy-compliant 16-char password.
          Once you authorize, buyer facts freeze and build jobs start.
        </>
      ),
      cta: {
        label: "🔐 Open Authorize Dialog",
        action: "authorize",
        tone: "blue",
      },
      hint: "This step allocates serials atomically — no two drives can ever collide.",
    },
    build: {
      icon: "⚙️",
      title: "Step 3 — Build in progress",
      body: (
        <>
          The pipeline is minting your {order.drives?.length || 0} drive(s). It
          runs <strong>36 verifier invariants + 53 pytest contracts</strong> on
          each drive before marking it buildable. This usually takes ~2 min.
          No action needed — the drawer polls every 4 s and updates automatically.
        </>
      ),
      cta: null,
      hint: order.drives?.some((d) => d.status === "build_failed")
        ? "One or more drives FAILED. Scroll to the Drives card for the exact invariant and a Retry button."
        : "Grab a coffee — the UI will light up Step 4 the second builds finish.",
    },
    download: {
      icon: "⬇️",
      title: "Step 4 — Download tarball + SHA",
      body: (
        <>
          Download the <code>.tar.gz</code> AND the <code>.sha256.txt</code>
          sidecar for every drive onto the ops Mac. Download links appear
          inside each Drive card below (click the serial). The next step
          won't unlock until you tick <em>Downloaded</em> below.
        </>
      ),
      cta: {
        label: marks.downloaded_at
          ? `✓ Downloaded (${new Date(marks.downloaded_at).toLocaleString()})`
          : "I've downloaded all drives",
        action: "mark-downloaded",
        tone: marks.downloaded_at ? "green-outline" : "blue",
      },
      hint: "If a drive is missing a SHA sidecar, hit Retry on that drive card instead of continuing.",
    },
    qc: {
      icon: "🧪",
      title: "Step 5 — Physical QC on a sample stick",
      body: (
        <>
          For each drive, flash a formatted exFAT USB and run the 10-item
          hardware checklist in the Drives card. All 10 ticks must be
          green before the drive can transition to <em>qc_passed</em>.
          If anything fails, the drive marks <em>qc_failed</em> and you
          can rebuild from Step 2.
        </>
      ),
      cta: null,
      hint: "The checklist is append-only — every tick is stamped with your user + UTC in the audit log.",
    },
    pack: {
      icon: "📦",
      title: "Step 6 — Pack the retail box",
      body: (
        <>
          Load the retail box bottom-up:
          <ol style={{ margin: "6px 0 8px 20px", padding: 0, fontSize: 12 }}>
            <li>Foam cradle</li>
            <li>USB drive (serial sticker up)</li>
            <li>Quick Start card on top of drive</li>
            <li>Pre-Insertion Checklist folded sleeve</li>
            <li>Thank You letter inside lid</li>
            <li>User Manual booklet in lid pocket</li>
            <li>Scratch-off password label on inside of lid</li>
            <li>Serial + PO stickers on outside of box</li>
          </ol>
          <div style={{
            marginTop: 8, padding: 8,
            background: "#FFFFFF", border: "1px dashed #CBD5E1",
            borderRadius: 6,
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#92400E", marginBottom: 6 }}>
              📄 Print these 4 retail PDFs (click to open):
            </div>
            <div style={{ display: "grid", gap: 4 }}>
              <a href="/retail_box/MOSAIC_ThankYou_Letter.pdf" target="_blank" rel="noreferrer"
                 data-testid="pack-print-thank-you"
                 style={{ fontSize: 11, color: "#2563EB", textDecoration: "none", padding: "4px 6px", background: "#DBEAFE", borderRadius: 4 }}>
                💌 Thank You Letter →
              </a>
              <a href="/retail_box/MOSAIC_QuickStart_Retail.pdf" target="_blank" rel="noreferrer"
                 data-testid="pack-print-quickstart"
                 style={{ fontSize: 11, color: "#2563EB", textDecoration: "none", padding: "4px 6px", background: "#DBEAFE", borderRadius: 4 }}>
                🚀 Quick Start Card →
              </a>
              <a href="/retail_box/MOSAIC_UserManual_Retail.pdf" target="_blank" rel="noreferrer"
                 data-testid="pack-print-manual"
                 style={{ fontSize: 11, color: "#2563EB", textDecoration: "none", padding: "4px 6px", background: "#DBEAFE", borderRadius: 4 }}>
                📘 User Manual →
              </a>
              <a href="/retail_box/MOSAIC_PreInsert_Checklist.pdf" target="_blank" rel="noreferrer"
                 data-testid="pack-print-checklist"
                 style={{ fontSize: 11, color: "#2563EB", textDecoration: "none", padding: "4px 6px", background: "#DBEAFE", borderRadius: 4 }}>
                ☑️ Pre-Insertion Checklist →
              </a>
            </div>
          </div>
        </>
      ),
      cta: {
        label: marks.packed_at
          ? `✓ Packed (${new Date(marks.packed_at).toLocaleString()})`
          : "I've packed the box",
        action: "mark-packed",
        tone: marks.packed_at ? "green-outline" : "blue",
      },
      hint: "The Thank You + Quick Start + Checklist are 1-page each on cardstock. The User Manual is a stapled booklet.",
    },
    ship: {
      icon: "🚚",
      title: "Step 7 — Record carrier + tracking",
      body: (
        <>
          Enter the carrier and the tracking number in the <strong>Shipping</strong>
          card below, then click <em>Mark Shipped</em>. The buyer receives
          an email with the tracking link + the Customer Deliverables PDF.
          This is a <strong>required</strong> field — no shipment, no close.
        </>
      ),
      cta: {
        label: "📨 Jump to Shipping Card",
        action: "focus-ship",
        tone: "blue",
      },
      hint: "UPS Ground with signature required ($18 to anywhere east of Mississippi) is the usual choice.",
    },
    close: {
      icon: "✅",
      title: "Step 8 — Close out",
      body: (
        <>
          Once the buyer confirms receipt (email reply, phone call, or the
          UPS "Delivered" webhook), click <em>Close Order</em> below. The
          order moves to a terminal state and the serial is marked lifetime-shipped.
        </>
      ),
      cta: {
        label: "✓ Close Order",
        action: "close",
        tone: "green",
      },
      hint: "Once closed, buyer facts stay archived forever for audit — nothing is deleted.",
    },
  };

  const c = cards[stepId];
  if (!c) return null;
  const toneBg =
    c.cta?.tone === "green" ? "#16A34A" :
    c.cta?.tone === "green-outline" ? "#FFFFFF" :
    c.cta?.tone === "blue" ? "#2563EB" : "#2563EB";
  const toneFg =
    c.cta?.tone === "green-outline" ? "#16A34A" : "#FFFFFF";
  const toneBorder =
    c.cta?.tone === "green-outline" ? "#16A34A" : "transparent";

  return (
    <div
      data-testid={`wf-action-card-${stepId}`}
      style={{
        padding: 16,
        background: "linear-gradient(180deg,#F8FAFC 0%,#FFFFFF 100%)",
        border: "1.5px solid #2563EB",
        borderRadius: 12,
        boxShadow: "0 2px 8px rgba(37,99,235,.08)",
      }}
    >
      <div style={{ display: "flex", gap: 12 }}>
        <div style={{
          fontSize: 28, lineHeight: 1,
          width: 44, height: 44, borderRadius: 10,
          background: "#DBEAFE", display: "flex",
          alignItems: "center", justifyContent: "center",
          flexShrink: 0,
        }}>{c.icon}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#1E40AF", marginBottom: 4 }}>
            {c.title}
          </div>
          <div style={{ fontSize: 12.5, color: "#334155", lineHeight: 1.55 }}>
            {c.body}
          </div>
        </div>
      </div>
      {c.cta && (
        <button
          data-testid={`wf-cta-${stepId}`}
          onClick={() => {
            if (c.cta.action === "mark-downloaded") onToggleMark("downloaded_at");
            else if (c.cta.action === "mark-packed") onToggleMark("packed_at");
            else onAction({ actionId: c.cta.action });
          }}
          style={{
            display: "block", width: "100%", marginTop: 12,
            padding: "11px 16px",
            background: toneBg, color: toneFg,
            border: `1.5px solid ${toneBorder}`,
            borderRadius: 8, fontSize: 13, fontWeight: 700,
            cursor: "pointer",
          }}
        >
          {c.cta.label}
        </button>
      )}
      {c.hint && (
        <div style={{
          marginTop: 10, padding: "8px 10px",
          background: "#FEF9C3", borderRadius: 6,
          fontSize: 11, color: "#713F12",
          borderLeft: "3px solid #EAB308",
        }}>
          💡 {c.hint}
        </div>
      )}
    </div>
  );
};

export default WorkflowStepper;
